import { useIsSoftCreditCheckApplication } from "@faire/deprecated-retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/useIsSoftCreditCheckApplication";
import { useIdvStatus } from "@faire/deprecated-retailer-visitor-shared/hooks/idv/useIdvStatus";
import { useFormattedOfferedMaxTerms } from "@faire/deprecated-retailer-visitor-shared/hooks/openWithFaire/useFormattedOfferedMaxTerms";
import { useGetFaireAcademyPageState } from "@faire/deprecated-retailer-visitor-shared/hooks/openWithFaire/useGetFaireAcademyPageState";
import { useGetStoreType } from "@faire/deprecated-retailer-visitor-shared/hooks/openWithFaire/useGetStoreType";
import { SignUpButton } from "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/OpenWithFaireFaireAcademyLandingPage/components/SignUpButton";
import {
  FAInnerWrapperColumn,
  FAOuterWrapper,
} from "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/OpenWithFaireFaireAcademyLandingPage/components/styles";
import { OWF_SECTION_NAME } from "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/OpenWithFaireFaireAcademyLandingPage/types";
import { Core, Decorative } from "@faire/design-tokens";
import { Calendar } from "@faire/slate/icons/Calendar";
import { PaymentPlan } from "@faire/slate/icons/PaymentPlan";
import { trackOpenWithFaireOwfApplicationCtaClick } from "@faire/web-api--source/events/openWithFaire/click/owfApplicationCta";
import { trackOpenWithFaireFaireAcademySectionView } from "@faire/web-api--source/events/openWithFaire/view/faireAcademySection";
import owfApplication from "@faire/web-api--source/routes/www/net-terms/owf-application";
import { useViewport } from "@faire/web/common/hooks/useViewport";
import { StoreType } from "@faire/web/common/StoreType";
import { Button } from "@faire/web/slate-deprecated/Button";
import { Flex, Column, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { SlideIn } from "@faire/web/ui/SlideIn/SlideIn";
import React from "react";
import styled from "styled-components";

export const GetTheInventoryYouNeed: React.FC = () => {
  const { pageState, isLoggedInNewApplied } = useGetFaireAcademyPageState();
  const ref = useDoActionWhenVisible(() =>
    trackOpenWithFaireFaireAcademySectionView(
      pageState,
      OWF_SECTION_NAME.GET_INVENTORY
    )
  );

  if (isLoggedInNewApplied) {
    return null;
  }

  return (
    <FAOuterWrapper $bgColor={Decorative.tint.neutral} ref={ref}>
      <FAInnerWrapperColumn>
        <PaymentTermsUpsell />
      </FAInnerWrapperColumn>
    </FAOuterWrapper>
  );
};

const PaymentTermsUpsell: React.FC = () => {
  const formattedOfferedMaxTerms = useFormattedOfferedMaxTerms();
  const { isLoggedInNewNotApplied } = useGetFaireAcademyPageState();

  return (
    <ResponsiveRowOrColumn>
      <Column justify="center">
        <SlideIn offset="50px">
          <Typography
            variant={{
              mobileAndAbove: "displaySSerifRegular",
              desktopAndAbove: "displayMSerifRegular",
            }}
          >
            <StrictLocalMsg
              id="a9pG/x"
              defaultMessage="Get up to {maxTermsMoney} in 60-day payment terms"
              description={{
                text: "Description of what Open with Faire payment terms increase helps with",
                img: "https://cdn.faire.com/fastly/ba2c3bb644077094207573a75b35557f1d849db6a405875547f5bce563016394.png",
              }}
              values={{
                maxTermsMoney: formattedOfferedMaxTerms,
              }}
            />
          </Typography>
        </SlideIn>
        <Spacer height={{ mobileAndAbove: "2x", desktopAndAbove: "3x" }} />
        <SlideIn offset="70px">
          <div className="max-w-[500px]">
            <Description />
          </div>
        </SlideIn>
        <SlideIn offset="70px">
          <ApplicationButton />
        </SlideIn>
      </Column>
      <Spacer size={{ mobileAndAbove: "4x", desktopAndAbove: "8x" }} />
      <SlideIn offset="50px">
        <RelativeDiv>
          <PaymentTermsUpsellImage />
          {isLoggedInNewNotApplied ? <PaymentTermsInfoCard /> : null}
        </RelativeDiv>
      </SlideIn>
    </ResponsiveRowOrColumn>
  );
};

const ApplicationButton: React.FC = () => {
  const { isSoftCreditCheckApplication } = useIsSoftCreditCheckApplication();
  const { hasPendingIdv, isPendingRiskAnalysis } = useIdvStatus();
  const { pageState, isLoggedOut, isLoggedInNewNotApplied } =
    useGetFaireAcademyPageState();

  const isPendingIdv = hasPendingIdv === "yes" || isPendingRiskAnalysis;

  switch (true) {
    case isPendingIdv:
      return null;
    case isLoggedInNewNotApplied:
      return (
        <>
          <Spacer height={{ mobileAndAbove: "2x", desktopAndAbove: "3x" }} />
          <StyledButton
            linkTo={owfApplication()}
            onClick={() =>
              trackOpenWithFaireOwfApplicationCtaClick(
                pageState,
                OWF_SECTION_NAME.GET_INVENTORY
              )
            }
          >
            {isSoftCreditCheckApplication ? (
              <StrictLocalMsg
                id="iAEGO3"
                defaultMessage="Apply in seconds"
                description={{
                  text: "Part of a nudge to encourage retailer to apply to the 'Open with Faire' program",
                  img: "https://cdn.faire.com/fastly/e60ca837a3b75bc04c55771e35d1cb25eb737f0b0bb55925b331c6d61fb39956.png",
                }}
              />
            ) : (
              <StrictLocalMsg
                id="5HBZBM"
                defaultMessage="Apply in minutes"
                description={{
                  text: "CTA text to the application form from the Open with Faire landing page, for logged in & new customers",
                  img: "https://cdn.faire.com/fastly/dbaa8da25fff5050bf679e93e754f10c481a41c4c97e3d13bd9a4b8a3cfe4b10.png",
                }}
              />
            )}
          </StyledButton>
        </>
      );
    case isLoggedOut:
      return (
        <>
          <Spacer height={{ mobileAndAbove: "2x", desktopAndAbove: "3x" }} />
          <SignUpButton source={OWF_SECTION_NAME.GET_INVENTORY} />
        </>
      );
    default:
      return null;
  }
};

const Description: React.FC = () => {
  const { hasPendingIdv, isPendingRiskAnalysis } = useIdvStatus();
  const isPendingIdv = hasPendingIdv === "yes" || isPendingRiskAnalysis;
  const formattedOfferedMaxTerms = useFormattedOfferedMaxTerms();
  if (isPendingIdv) {
    return (
      <Typography>
        <StrictLocalMsg
          id="yoPAg6"
          defaultMessage="We’re in the process of verifying your account. Once your account is verified, you can apply for payment terms."
          description={{
            text: "Faire is in the process of verifying your account. Once your account is verified, you can apply for payment terms.",
            img: "https://cdn.faire.com/fastly/95002ea0becff97d5cc17e816cefe6484429a89c2d74857d99e777d38f39d28c.png",
          }}
        />
      </Typography>
    );
  }

  return (
    <Typography
      variant={{
        mobileAndAbove: "paragraphSansRegular",
        desktopAndAbove: "subheadingSansRegular",
      }}
    >
      <StrictLocalMsg
        id="cUeM4K"
        defaultMessage="We know stocking a new retail store can be expensive, and financing is often hard to get. Open with Faire offers new stores up to {maxTermsMoney} in 60-day payment terms, so they can stock their shelves without worry."
        description={{
          text: "Description of what Open with Faire payment terms increase helps with",
          img: "https://cdn.faire.com/fastly/7ea12df01ff4a012083fdf1538203f16b0e3ba9ee084ae195beae0a77cda9bc6.png",
        }}
        values={{
          maxTermsMoney: formattedOfferedMaxTerms,
        }}
      />
    </Typography>
  );
};

const PaymentTermsUpsellImage: React.FC = () => {
  const { storeType, shouldShowPartialRetailersBasedOnStoreType } =
    useGetStoreType();
  let imageSrc =
    "https://cdn.faire.com/fastly/498a065edd53cf888835902da18203f2dfa4c62879cf0b9c22412aace0022428.png";

  if (shouldShowPartialRetailersBasedOnStoreType) {
    if (storeType === StoreType.SPA_OR_SALON) {
      imageSrc =
        "https://cdn.faire.com/fastly/314958f0cfe8a5bbf3e295aef8b5b92a30002f0117501ca783ec9f82445475bf.png";
    } else if (storeType === StoreType.CAFE_OR_RESTAURANT) {
      imageSrc =
        "https://cdn.faire.com/fastly/cabde8f8afd9dcbf1c844f4f757933afefcdb2eb7a943ec831cf553a1d49a8a1.png";
    }
  }

  return <StyledImage src={imageSrc} alt="" />;
};

const PaymentTermsInfoCard: React.FC = () => {
  const formattedOfferedMaxTerms = useFormattedOfferedMaxTerms();
  return (
    <PaddedColumn gap="2x">
      <InfoCardLine
        icon="payment"
        text={
          <StrictLocalMsg
            id="pdMv3I"
            defaultMessage="Apply for up to {maxTermsMoney} in terms"
            values={{ maxTermsMoney: formattedOfferedMaxTerms }}
            description={{
              text: "A prompt to apply for more payment terms on Faire",
              img: "https://cdn.faire.com/fastly/7ea12df01ff4a012083fdf1538203f16b0e3ba9ee084ae195beae0a77cda9bc6.png",
            }}
          />
        }
      />
      <InfoCardLine
        icon="calendar"
        text={
          <StrictLocalMsg
            id="3GmiF4"
            defaultMessage="Buy now, pay 60 days later"
            description={{
              text: "A brief explanation of payment terms",
              img: "https://cdn.faire.com/fastly/7ea12df01ff4a012083fdf1538203f16b0e3ba9ee084ae195beae0a77cda9bc6.png",
            }}
          />
        }
      />
    </PaddedColumn>
  );
};

const InfoCardLine: React.FC<{
  text: React.ReactNode;
  icon: "payment" | "calendar";
}> = ({ icon, text }) => {
  const { isDesktopAndAbove } = useViewport();
  const size = isDesktopAndAbove ? "32px" : undefined;
  return (
    <Row gap="2x" align="center">
      {icon === "payment" ? (
        <PaymentPlan width={size} height={size} />
      ) : (
        <Calendar width={size} height={size} />
      )}
      <Typography
        variant={{
          desktopAndAbove: "subheadingSansMedium",
          mobileAndAbove: "paragraphSansMedium",
        }}
      >
        {text}
      </Typography>
    </Row>
  );
};

const ResponsiveRowOrColumn = styled(Flex).attrs({
  direction: { mobileAndAbove: "column", tabletAndAbove: "row" },
})``;

const StyledButton = styled(Button)`
  width: fit-content;

  ${responsiveMediaQueries.mobile} {
    width: 100%;
  }
`;

const StyledImage = styled.img`
  width: 455px;
  height: 455px;
  max-width: max-content;

  ${responsiveMediaQueries.tablet} {
    width: 300px;
    height: 300px;
  }
  ${responsiveMediaQueries.mobile} {
    width: 100%;
    height: 100%;
  }
`;

const RelativeDiv = styled.div`
  position: relative;
`;

const PaddedColumn = styled(Column)`
  position: absolute;
  background-color: ${Core.surface.inverse};
  max-width: 100%;
  min-width: 90%;
  box-shadow: 0px 4px 10px 0px #00000026;

  ${responsiveMediaQueries.tabletAndBelow} {
    padding: ${getSpacing("2x")};
    bottom: -24px;
    left: 24px;
    right: 24px;
  }

  ${responsiveMediaQueries.desktopAndAbove} {
    padding: ${getSpacing("4x")};
    top: 59%;
    left: -48px;
  }
`;
